import React from "react";
import {
  TableBody,
  TableRow,
  TableCell,
  Grid,
  IconButton,
  Chip,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";

const PLACEHOLDER_JERSEY_URL =
  "https://engagecraftfantasydev.blob.core.windows.net/teams/shirts/placeholder@1x.png";

const getStatusChipProps = (status) => {
  const normalizedStatus = status?.toLowerCase() || "available";
  switch (normalizedStatus) {
    case "available":
      return { label: "Available", color: "success", variant: "filled" };
    case "injured":
      return { label: "Injured", color: "error", variant: "filled" };
    case "suspended":
      return { label: "Suspended", color: "warning", variant: "filled" };
    case "unknown":
      return { label: "Unknown", color: "default", variant: "outlined" };
    default:
      return { label: normalizedStatus, color: "default", variant: "filled" };
  }
};

const getPositionChipProps = (position) => {
  switch (position) {
    case "GK":
      return { label: "GK", color: "info", variant: "filled" };
    case "DEF":
      return { label: "DEF", color: "primary", variant: "filled" };
    case "MID":
      return { label: "MID", color: "secondary", variant: "filled" };
    case "STR":
    case "FWD":
      return {
        label: position === "STR" ? "STR" : "FWD",
        color: "error",
        variant: "filled",
      };
    default:
      return { label: position || "-", color: "default", variant: "outlined" };
  }
};

const getVisibilityChipProps = (isVisible) => {
  if (isVisible) {
    return { label: "Visible", color: "success", variant: "outlined" };
  }
  return { label: "Hidden", color: "error", variant: "outlined" };
};

const getHasLeftChipProps = (hasLeft) => {
  if (hasLeft) {
    return { label: "Left", color: "warning", variant: "outlined" };
  }
  return { label: "Active", color: "success", variant: "outlined" };
};

const PlayersTableBody = ({ players, onMenuOpen }) => {
  return (
    <TableBody
      sx={{
        "& td, & th": {
          color: "#6A707E",
          fontWeight: 500,
          fontSize: "16px",
        },
      }}
    >
      {players.map((player) => {
        const statusProps = getStatusChipProps(player.availabilityDisplay);
        const positionProps = getPositionChipProps(player.position);
        const visibilityProps = getVisibilityChipProps(player.isVisible);
        const hasLeftProps = getHasLeftChipProps(player.hasLeft);
        return (
          <TableRow
            key={player.id}
            sx={{
              backgroundColor: "white",
              borderRadius: 2,
              boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.05)",
              "&:last-child td, &:last-child th": { borderBottom: 0 },
            }}
          >
            <TableCell>
              <img
                src={player.jerseyUrl || PLACEHOLDER_JERSEY_URL}
                alt={`${player.team} jersey`}
                style={{ width: 40, height: 40, objectFit: "contain" }}
                onError={(e) => {
                  if (e.target.src !== PLACEHOLDER_JERSEY_URL) {
                    e.target.src = PLACEHOLDER_JERSEY_URL;
                  }
                }}
              />
            </TableCell>
            <TableCell sx={{ pl: 1 }}>
              <span style={{ fontWeight: 600 }}>
                {player.firstName} {player.lastName}
              </span>
            </TableCell>
            <TableCell>
              <Chip size="small" {...statusProps} />
            </TableCell>
            <TableCell>
              <Grid container alignItems="center" spacing={1}>
                {player.flagUrl && (
                  <Grid item>
                    <img
                      src={player.flagUrl}
                      alt={`${player.team} flag`}
                      style={{
                        width: 24,
                        height: 24,
                        borderRadius: "50%",
                        objectFit: "cover",
                      }}
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                  </Grid>
                )}
                <Grid item>{player.team}</Grid>
              </Grid>
            </TableCell>
            <TableCell>
              <Chip size="small" {...positionProps} />
            </TableCell>
            <TableCell>
              {player.price
                ? `£${parseFloat(player.price).toFixed(1)}m`
                : "£0.0m"}
            </TableCell>
            <TableCell>
              {player.percentSelected?.toFixed(1) || 0}%
            </TableCell>
            <TableCell>
              <Chip size="small" {...visibilityProps} />
            </TableCell>
            <TableCell>
              <Chip size="small" {...hasLeftProps} />
            </TableCell>
            <TableCell align="right">
              <IconButton onClick={(e) => onMenuOpen(e, player.id)}>
                <MoreVertIcon />
              </IconButton>
            </TableCell>
          </TableRow>
        );
      })}
    </TableBody>
  );
};

export default PlayersTableBody;
